import type { OperatorReinforcementOffer } from '@groupe-reca/contracts'
import { useQueryClient } from '@tanstack/react-query'
import { Handshake } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useOnline } from '@/shared/hooks/useOnline'
import { missionKeys } from '../../application/missionQueries'
import { operationKeys, useReinforcementOffers } from '../../application/operationsQueries'
import { useOperator } from '../../application/OperatorServices'
import { claimOffer } from '../../application/reinforcement/claimOffer'
import { itemAddress, serverMessage } from '../../domain/fieldText'

type LocalClaim = { kind: 'sending' } | { kind: 'pending' } | { kind: 'taken'; by: string | null } | { kind: 'error'; text: string }

function takenText(by: string | null): string {
  return by ? `Déjà prise en charge par ${by}` : 'Déjà prise en charge'
}

/**
 * « Renfort disponible » : résidences offertes par le bureau. La prise en charge exige une mission en cours dans
 * l'opération (sinon « Commencer le renfort »); un seul responsable, décidé par le serveur. Hors connexion,
 * l'attribution est indisponible.
 */
export function ReinforcementPanel({ missionId }: { missionId: string | null }) {
  const { userId, repository, store } = useOperator()
  const queryClient = useQueryClient()
  const online = useOnline()
  const offers = useReinforcementOffers()
  const [claims, setClaims] = useState<Record<string, LocalClaim>>({})
  const list = offers.data?.offers ?? []

  async function take(offer: OperatorReinforcementOffer) {
    setClaims((current) => ({ ...current, [offer.offerId]: { kind: 'sending' } }))
    const outcome = await claimOffer(store, repository, offer)
    const next: LocalClaim | null =
      outcome.kind === 'confirmed'
        ? null
        : outcome.kind === 'pending'
          ? { kind: 'pending' }
          : outcome.kind === 'taken'
            ? { kind: 'taken', by: outcome.by }
            : { kind: 'error', text: serverMessage(outcome.code) }
    setClaims((current) => {
      const rest = Object.fromEntries(Object.entries(current).filter(([offerId]) => offerId !== offer.offerId))
      return next ? { ...rest, [offer.offerId]: next } : rest
    })
    await queryClient.invalidateQueries({ queryKey: operationKeys.offers(userId) })
    if (missionId) await queryClient.invalidateQueries({ queryKey: missionKeys.view(userId, missionId) })
  }

  if (list.length === 0) {
    return (
      <section className="op-reinforcement" aria-label="Renfort disponible">
        <h2>
          <Handshake aria-hidden="true" /> Renfort disponible
        </h2>
        <p className="op-muted">{offers.isError ? 'Offres non relues : réseau indisponible.' : 'Aucune résidence offerte pour l’instant.'}</p>
      </section>
    )
  }

  return (
    <section className="op-reinforcement" aria-label="Renfort disponible">
      <h2>
        <Handshake aria-hidden="true" /> Renfort disponible
      </h2>
      {!online ? <p className="op-muted">Hors connexion : attribution indisponible.</p> : null}
      <ul className="op-reinforcement__list">
        {list.map((offer) => {
          const local = claims[offer.offerId]
          const address = itemAddress(offer.snapshot)
          let state: ReactNode
          if (offer.status === 'CLAIMED' && offer.claimedByMe) {
            state = <span className="op-chip op-chip--done">Attribution confirmée</span>
          } else if (offer.status === 'CLAIMED' || local?.kind === 'taken') {
            state = (
              <span className="op-chip op-chip--problem">
                {takenText(local?.kind === 'taken' ? local.by : offer.claimedByName)}
              </span>
            )
          } else if (local?.kind === 'sending' || local?.kind === 'pending') {
            state = <span className="op-chip op-chip--todo">Attribution en attente</span>
          } else if (!offer.canClaim) {
            state = (
              <span className="op-muted op-reinforcement__hint">
                {missionId ? 'Non disponible pour votre mission' : 'Commencez le renfort pour la prendre'}
              </span>
            )
          } else {
            state = null
          }
          const canTake =
            offer.canClaim && offer.status === 'ACTIVE' && online && local?.kind !== 'sending' && local?.kind !== 'taken'
          return (
            <li key={offer.offerId}>
              <span className="op-reinforcement__address">
                <span className="op-swatch" style={{ background: offer.routeColor ?? undefined }} aria-hidden="true" />
                <span>
                  <strong>{address}</strong>
                  <small>
                    {offer.routeName ?? 'Route'}
                    {offer.isRetry ? ' · nouvelle tentative' : ''}
                  </small>
                </span>
              </span>
              {state}
              {canTake ? (
                <button
                  type="button"
                  className="field-button field-button--primary"
                  aria-label={`Prendre en charge le ${address}`}
                  onClick={() => void take(offer)}
                >
                  {local?.kind === 'pending' ? 'Renvoyer' : 'Prendre en charge'}
                </button>
              ) : null}
              {local?.kind === 'error' ? (
                <p className="field-reason__error" role="alert">
                  {local.text}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
